import { useMemo, useState } from 'react';
import { CalendarClock, Loader2, LogIn, LogOut, Plus, TimerReset, Truck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDateTime, type DepotAppointment, type DepotGateTransaction, type DepotSite, useDepotList } from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type CheckInFormState = {
  appointmentId: string;
  depotId: string;
  containerNo: string;
  lineOperator: string;
  containerSizeType: string;
  loadStatus: 'empty' | 'laden';
  truckPlate: string;
  driverName: string;
  haulierName: string;
  slotCode: string;
};

type CheckOutFormState = {
  appointmentId: string;
  depotId: string;
  containerNo: string;
  truckPlate: string;
  driverName: string;
  haulierName: string;
  releaseRef: string;
};

type AppointmentFormState = {
  depotId: string;
  appointmentType: 'gate_in' | 'gate_out';
  scheduledStartAt: string;
  scheduledEndAt: string;
  containerNo: string;
  lineOperator: string;
  containerSizeType: string;
  truckPlate: string;
  driverName: string;
  haulierName: string;
  releaseRef: string;
  note: string;
};

const EMPTY_IN: CheckInFormState = {
  appointmentId: '',
  depotId: '',
  containerNo: '',
  lineOperator: '',
  containerSizeType: '40HC',
  loadStatus: 'empty',
  truckPlate: '',
  driverName: '',
  haulierName: '',
  slotCode: '',
};

const EMPTY_OUT: CheckOutFormState = {
  appointmentId: '',
  depotId: '',
  containerNo: '',
  truckPlate: '',
  driverName: '',
  haulierName: '',
  releaseRef: '',
};

const EMPTY_APPOINTMENT: AppointmentFormState = {
  depotId: '',
  appointmentType: 'gate_in',
  scheduledStartAt: '',
  scheduledEndAt: '',
  containerNo: '',
  lineOperator: '',
  containerSizeType: '',
  truckPlate: '',
  driverName: '',
  haulierName: '',
  releaseRef: '',
  note: '',
};

function isLateAppointment(item: DepotAppointment): boolean {
  if (item.status !== 'booked') return false;
  const scheduledEnd = new Date(item.scheduled_end_at);
  return !Number.isNaN(scheduledEnd.getTime()) && scheduledEnd.getTime() < Date.now();
}

export default function DepotGatePage() {
  const { items, loading, error, refresh } = useDepotList<DepotGateTransaction>('/depot/gate/queue?limit=50');
  const { items: depots } = useDepotList<DepotSite>('/depot/depots?limit=100');
  const { items: appointments, refresh: refreshAppointments } = useDepotList<DepotAppointment>('/depot/appointments?limit=12');
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [checkInForm, setCheckInForm] = useState<CheckInFormState>(EMPTY_IN);
  const [checkOutForm, setCheckOutForm] = useState<CheckOutFormState>(EMPTY_OUT);
  const [appointmentForm, setAppointmentForm] = useState<AppointmentFormState>(EMPTY_APPOINTMENT);
  const [saving, setSaving] = useState(false);

  const appointmentSummary = useMemo(() => {
    const booked = appointments.filter((item) => item.status === 'booked').length;
    const checkedIn = appointments.filter((item) => item.status === 'checked_in').length;
    const pending = appointments.filter((item) => ['booked', 'checked_in'].includes(item.status)).length;
    return { booked, checkedIn, pending };
  }, [appointments]);

  const hasDepots = depots.length > 0;

  const pageError: DataErrorDetail | null = error
    ? { endpoint: '/depot/gate/queue', message: error, status: 500, code: 'DEPOT_GATE_LOAD_FAILED' }
    : null;

  const handleCheckIn = async () => {
    if (!checkInForm.depotId || !checkInForm.containerNo.trim()) {
      toast.error('Depot and container number are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/gate/check-in', {
        appointmentId: checkInForm.appointmentId || undefined,
        depotId: checkInForm.depotId,
        containerNo: checkInForm.containerNo.trim().toUpperCase(),
        lineOperator: checkInForm.lineOperator.trim() || undefined,
        containerSizeType: checkInForm.containerSizeType.trim() || undefined,
        loadStatus: checkInForm.loadStatus,
        truckPlate: checkInForm.truckPlate.trim() || undefined,
        driverName: checkInForm.driverName.trim() || undefined,
        haulierName: checkInForm.haulierName.trim() || undefined,
        slotCode: checkInForm.slotCode.trim() || undefined,
      });
      toast.success('Gate-in completed');
      setCheckInOpen(false);
      setCheckInForm(EMPTY_IN);
      await Promise.all([refresh(), refreshAppointments()]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to check in container');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckOut = async () => {
    if (!checkOutForm.depotId || !checkOutForm.containerNo.trim()) {
      toast.error('Depot and container number are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/gate/check-out', {
        appointmentId: checkOutForm.appointmentId || undefined,
        depotId: checkOutForm.depotId,
        containerNo: checkOutForm.containerNo.trim().toUpperCase(),
        truckPlate: checkOutForm.truckPlate.trim() || undefined,
        driverName: checkOutForm.driverName.trim() || undefined,
        haulierName: checkOutForm.haulierName.trim() || undefined,
        releaseRef: checkOutForm.releaseRef.trim() || undefined,
      });
      toast.success('Gate-out completed');
      setCheckOutOpen(false);
      setCheckOutForm(EMPTY_OUT);
      await Promise.all([refresh(), refreshAppointments()]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to check out container');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (!appointmentForm.depotId || !appointmentForm.scheduledStartAt || !appointmentForm.scheduledEndAt) {
      toast.error('Depot and appointment window are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/appointments', {
        depotId: appointmentForm.depotId,
        appointmentType: appointmentForm.appointmentType,
        scheduledStartAt: new Date(appointmentForm.scheduledStartAt).toISOString(),
        scheduledEndAt: new Date(appointmentForm.scheduledEndAt).toISOString(),
        containerNo: appointmentForm.containerNo.trim().toUpperCase() || undefined,
        lineOperator: appointmentForm.lineOperator.trim() || undefined,
        containerSizeType: appointmentForm.containerSizeType.trim() || undefined,
        truckPlate: appointmentForm.truckPlate.trim() || undefined,
        driverName: appointmentForm.driverName.trim() || undefined,
        haulierName: appointmentForm.haulierName.trim() || undefined,
        releaseRef: appointmentForm.releaseRef.trim() || undefined,
        note: appointmentForm.note.trim() || undefined,
      });
      toast.success('Appointment created');
      setAppointmentOpen(false);
      setAppointmentForm(EMPTY_APPOINTMENT);
      await refreshAppointments();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create appointment');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAppointmentCheckedIn = async (appointmentId: string) => {
    setSaving(true);
    try {
      await api.post(`/depot/appointments/${appointmentId}/check-in`, {});
      toast.success('Appointment checked in');
      await refreshAppointments();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to check in appointment');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    setSaving(true);
    try {
      await api.post(`/depot/appointments/${appointmentId}/cancel`, {});
      toast.success('Appointment cancelled');
      await refreshAppointments();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel appointment');
    } finally {
      setSaving(false);
    }
  };

  const useAppointmentForGate = (item: DepotAppointment) => {
    const common = {
      appointmentId: item.id,
      depotId: item.depot_id,
      containerNo: item.container_no ?? '',
      truckPlate: item.truck_plate ?? '',
      driverName: item.driver_name ?? '',
      haulierName: item.haulier_name ?? '',
    };

    if (item.appointment_type === 'gate_out') {
      setCheckOutForm({
        ...EMPTY_OUT,
        ...common,
        releaseRef: item.release_ref ?? '',
      });
      setCheckOutOpen(true);
      return;
    }

    setCheckInForm({
      ...EMPTY_IN,
      ...common,
      lineOperator: item.line_operator ?? '',
      containerSizeType: item.container_size_type ?? '40HC',
    });
    setCheckInOpen(true);
  };

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Gate" onRetry={refresh} />}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Gate Desk</h2>
          <p className="text-sm text-muted-foreground">
            Register gate-in, gate-out, and today truck/container queue.
          </p>
          {!hasDepots && (
            <p className="text-sm text-amber-700">No depot site configured yet. Add a depot master record before creating appointments or gate transactions.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setAppointmentOpen(true)} disabled={!hasDepots}>
            <CalendarClock className="mr-1.5 h-4 w-4" />
            Appointment
          </Button>
          <Button variant="outline" onClick={() => setCheckOutOpen(true)} disabled={!hasDepots}>
            <LogOut className="mr-1.5 h-4 w-4" />
            Gate Out
          </Button>
          <Button onClick={() => setCheckInOpen(true)} disabled={!hasDepots}>
            <Plus className="mr-1.5 h-4 w-4" />
            Gate In
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Booked Today</span>
            <CalendarClock className="h-4 w-4 text-sky-700" />
          </div>
          <div className="text-2xl font-bold">{appointmentSummary.booked}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Checked In</span>
            <LogIn className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="text-2xl font-bold">{appointmentSummary.checkedIn}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Pending Queue</span>
            <TimerReset className="h-4 w-4 text-orange-700" />
          </div>
          <div className="text-2xl font-bold">{appointmentSummary.pending}</div>
        </div>
      </div>

      <DepotGridPanel title="Appointment Queue" subtitle="Booking, check-in, cancellation, and gate execution in one dense queue.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Appointment</th>
              <th className={depotThClassName}>Type / Status</th>
              <th className={depotThClassName}>Window</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Truck / Driver</th>
              <th className={depotThClassName}>Release</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {appointments.length === 0 ? (
              <DepotEmptyRow colSpan={7}>No appointments scheduled yet.</DepotEmptyRow>
            ) : (
              appointments.map((item) => {
                const late = isLateAppointment(item);
                return (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className={depotTdClassName}>
                      <div className="font-mono font-semibold">{item.appointment_no}</div>
                      <div className="text-xs text-muted-foreground">{item.depot_code ?? '—'}</div>
                    </td>
                    <td className={depotTdClassName}>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant={item.appointment_type === 'gate_in' ? 'secondary' : 'outline'}>
                          {item.appointment_type}
                        </Badge>
                        <Badge variant={late ? 'destructive' : item.status === 'completed' ? 'outline' : 'secondary'}>
                          {late ? 'late' : item.status}
                        </Badge>
                      </div>
                    </td>
                    <td className={`${depotTdClassName} text-xs text-muted-foreground`}>
                      <div>{formatDateTime(item.scheduled_start_at)}</div>
                      <div>{formatDateTime(item.scheduled_end_at)}</div>
                    </td>
                    <td className={`${depotTdClassName} font-mono`}>{item.container_no ?? 'TBC'}</td>
                    <td className={depotTdClassName}>
                      <div>{item.truck_plate ?? 'Truck TBC'}</div>
                      <div className="text-xs text-muted-foreground">{item.driver_name ?? 'Driver TBC'} · {item.haulier_name ?? 'Haulier TBC'}</div>
                    </td>
                    <td className={depotTdClassName}>{item.release_ref ?? '—'}</td>
                    <td className={depotTdClassName}>
                      <div className="flex flex-wrap gap-2">
                        {item.status === 'booked' && (
                          <Button size="sm" variant="outline" onClick={() => handleMarkAppointmentCheckedIn(item.id)} disabled={saving}>
                            <LogIn className="mr-1.5 h-3.5 w-3.5" />
                            Checked In
                          </Button>
                        )}
                        {item.status !== 'cancelled' && item.status !== 'completed' && (
                          <Button size="sm" variant="outline" onClick={() => useAppointmentForGate(item)}>
                            <Truck className="mr-1.5 h-3.5 w-3.5" />
                            Use Gate
                          </Button>
                        )}
                        {item.status !== 'completed' && item.status !== 'cancelled' && (
                          <Button size="sm" variant="ghost" onClick={() => handleCancelAppointment(item.id)} disabled={saving}>
                            <XCircle className="mr-1.5 h-3.5 w-3.5" />
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Gate Transactions" subtitle="Completed gate actions, truck identity, release and rejection reason.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Gate Txn</th>
              <th className={depotThClassName}>Type</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Depot</th>
              <th className={depotThClassName}>Truck / Driver</th>
              <th className={depotThClassName}>Performed</th>
              <th className={depotThClassName}>Release / Rejection</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <DepotEmptyRow colSpan={7}>Loading gate transactions...</DepotEmptyRow>
            ) : items.length === 0 ? (
              <DepotEmptyRow colSpan={7}>No gate transactions yet.</DepotEmptyRow>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.gate_txn_no}</td>
                  <td className={depotTdClassName}>
                    <Badge variant={item.gate_type === 'gate_in' ? 'secondary' : 'outline'}>{item.gate_type}</Badge>
                  </td>
                  <td className={`${depotTdClassName} font-mono`}>{item.container_no}</td>
                  <td className={depotTdClassName}>{item.depot_code ?? '—'}</td>
                  <td className={depotTdClassName}>
                    <div>{item.truck_plate ?? 'Truck TBC'}</div>
                    <div className="text-xs text-muted-foreground">{item.driver_name ?? 'Driver TBC'} · {item.haulier_name ?? 'Haulier TBC'}</div>
                  </td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.performed_at)}</td>
                  <td className={depotTdClassName}>
                    <div>{item.release_ref ? `Release ${item.release_ref}` : '—'}</div>
                    {item.rejection_reason ? <div className="text-xs text-rose-600">{item.rejection_reason}</div> : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <Dialog open={appointmentOpen} onOpenChange={setAppointmentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Appointment</DialogTitle>
            <DialogDescription>
              Schedule an inbound or outbound depot slot before the truck reaches the gate desk.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Select value={appointmentForm.depotId} onValueChange={(value) => setAppointmentForm((current) => ({ ...current, depotId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select depot" />
              </SelectTrigger>
              <SelectContent>
                {depots.map((depot) => (
                  <SelectItem key={depot.id} value={depot.id}>{depot.depot_code} · {depot.depot_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={appointmentForm.appointmentType} onValueChange={(value: 'gate_in' | 'gate_out') => setAppointmentForm((current) => ({ ...current, appointmentType: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gate_in">Gate In</SelectItem>
                <SelectItem value="gate_out">Gate Out</SelectItem>
              </SelectContent>
            </Select>
            <Input type="datetime-local" value={appointmentForm.scheduledStartAt} onChange={(event) => setAppointmentForm((current) => ({ ...current, scheduledStartAt: event.target.value }))} />
            <Input type="datetime-local" value={appointmentForm.scheduledEndAt} onChange={(event) => setAppointmentForm((current) => ({ ...current, scheduledEndAt: event.target.value }))} />
            <Input placeholder="Container No" value={appointmentForm.containerNo} onChange={(event) => setAppointmentForm((current) => ({ ...current, containerNo: event.target.value }))} />
            <Input placeholder="Line Operator" value={appointmentForm.lineOperator} onChange={(event) => setAppointmentForm((current) => ({ ...current, lineOperator: event.target.value }))} />
            <Input placeholder="Container Size Type" value={appointmentForm.containerSizeType} onChange={(event) => setAppointmentForm((current) => ({ ...current, containerSizeType: event.target.value }))} />
            <Input placeholder="Truck Plate" value={appointmentForm.truckPlate} onChange={(event) => setAppointmentForm((current) => ({ ...current, truckPlate: event.target.value }))} />
            <Input placeholder="Driver Name" value={appointmentForm.driverName} onChange={(event) => setAppointmentForm((current) => ({ ...current, driverName: event.target.value }))} />
            <Input placeholder="Haulier Name" value={appointmentForm.haulierName} onChange={(event) => setAppointmentForm((current) => ({ ...current, haulierName: event.target.value }))} />
            {appointmentForm.appointmentType === 'gate_out' && (
              <Input placeholder="Release Ref" value={appointmentForm.releaseRef} onChange={(event) => setAppointmentForm((current) => ({ ...current, releaseRef: event.target.value }))} />
            )}
            <Input placeholder="Internal note" value={appointmentForm.note} onChange={(event) => setAppointmentForm((current) => ({ ...current, note: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppointmentOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateAppointment} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CalendarClock className="mr-1.5 h-4 w-4" />}
              Create Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={checkInOpen} onOpenChange={setCheckInOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gate In</DialogTitle>
            <DialogDescription>
              Register container arrival and optionally bind it to a booked appointment.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Select value={checkInForm.depotId} onValueChange={(value) => setCheckInForm((current) => ({ ...current, depotId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select depot" />
              </SelectTrigger>
              <SelectContent>
                {depots.map((depot) => (
                  <SelectItem key={depot.id} value={depot.id}>{depot.depot_code} · {depot.depot_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Container No" value={checkInForm.containerNo} onChange={(event) => setCheckInForm((current) => ({ ...current, containerNo: event.target.value }))} />
            <Input placeholder="Line Operator" value={checkInForm.lineOperator} onChange={(event) => setCheckInForm((current) => ({ ...current, lineOperator: event.target.value }))} />
            <Input placeholder="Container Size Type" value={checkInForm.containerSizeType} onChange={(event) => setCheckInForm((current) => ({ ...current, containerSizeType: event.target.value }))} />
            <Select value={checkInForm.loadStatus} onValueChange={(value: 'empty' | 'laden') => setCheckInForm((current) => ({ ...current, loadStatus: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="empty">Empty</SelectItem>
                <SelectItem value="laden">Laden</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Truck Plate" value={checkInForm.truckPlate} onChange={(event) => setCheckInForm((current) => ({ ...current, truckPlate: event.target.value }))} />
            <Input placeholder="Driver Name" value={checkInForm.driverName} onChange={(event) => setCheckInForm((current) => ({ ...current, driverName: event.target.value }))} />
            <Input placeholder="Haulier Name" value={checkInForm.haulierName} onChange={(event) => setCheckInForm((current) => ({ ...current, haulierName: event.target.value }))} />
            <Input placeholder="Initial Slot Code (Optional)" value={checkInForm.slotCode} onChange={(event) => setCheckInForm((current) => ({ ...current, slotCode: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckInOpen(false)}>Cancel</Button>
            <Button onClick={handleCheckIn} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <LogIn className="mr-1.5 h-4 w-4" />}
              Confirm Gate In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={checkOutOpen} onOpenChange={setCheckOutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gate Out</DialogTitle>
            <DialogDescription>
              Record container release from the depot gate and close any linked appointment.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Select value={checkOutForm.depotId} onValueChange={(value) => setCheckOutForm((current) => ({ ...current, depotId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select depot" />
              </SelectTrigger>
              <SelectContent>
                {depots.map((depot) => (
                  <SelectItem key={depot.id} value={depot.id}>{depot.depot_code} · {depot.depot_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Container No" value={checkOutForm.containerNo} onChange={(event) => setCheckOutForm((current) => ({ ...current, containerNo: event.target.value }))} />
            <Input placeholder="Release Ref" value={checkOutForm.releaseRef} onChange={(event) => setCheckOutForm((current) => ({ ...current, releaseRef: event.target.value }))} />
            <Input placeholder="Truck Plate" value={checkOutForm.truckPlate} onChange={(event) => setCheckOutForm((current) => ({ ...current, truckPlate: event.target.value }))} />
            <Input placeholder="Driver Name" value={checkOutForm.driverName} onChange={(event) => setCheckOutForm((current) => ({ ...current, driverName: event.target.value }))} />
            <Input placeholder="Haulier Name" value={checkOutForm.haulierName} onChange={(event) => setCheckOutForm((current) => ({ ...current, haulierName: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckOutOpen(false)}>Cancel</Button>
            <Button onClick={handleCheckOut} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <LogOut className="mr-1.5 h-4 w-4" />}
              Confirm Gate Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
